import React, { useState, useEffect } from 'react';
import { getRiskBandLabel } from '../../../utils/bands';
import HelpTooltip from '../../../shared/components/HelpTooltip';
import apiConfig from '../../../config/apiConfig';
import { authHeaders } from '../../../services/authHeaders';
import { resolveCurrentSessionId } from '../../../services/currentSession';
import {
  ShieldAlert,
  Globe,
  TrendingUp,
  XCircle,
  Info,
  Target,
  ChevronRight,
  Users,
  DollarSign,
  Cpu,
  Shield,
  Briefcase,
  Globe2,
  AlertCircle,
  ArrowUp,
  Minus,
  RefreshCw
} from 'lucide-react';

// A null score is a category (or factor) no data source measures. Show it as such, never as a number.
export const NOT_MEASURED = 'Not measured';
const NOT_MEASURED_LEVEL = -1;
export const isMeasured = (score: unknown): score is number =>
  typeof score === 'number' && Number.isFinite(score);

interface OutsidePressureFactor {
  id: string;
  name: string;
  currentScore: number | null;
  maxScore: number;
  impact: 'Critical' | 'High' | 'Medium' | 'Low' | 'Future Critical';
  trend: string;
  measurement: string;
}

interface OutsidePressureCategory {
  description: string;
  factors: OutsidePressureFactor[];
}

interface CategoryScore {
  score: number | null;
  riskLevel: string;
  factorCount: number;
  criticalFactors: number;
  trendingUp: number;
}

interface ExecutiveSummary {
  overallAssessment: string;
  highRiskAreas: string[];
  emergingThreats: string[];
  riskAlignment: string;
  keyInsights: string[];
  executiveRecommendation: string;
}

interface OutsidePressuresData {
  overallScore: number | null;
  riskLevel: string;
  categories: Record<string, OutsidePressureCategory>;
  categoryScores: Record<string, CategoryScore>;
  executiveSummary: ExecutiveSummary | null;
  totalFactors: number;
  criticalFactors: number;
  emergingThreats: number;
}

interface OutsidePressuresCenterProps {
  assessmentId?: string;
}

const OutsidePressuresCenter: React.FC<OutsidePressuresCenterProps> = ({
  assessmentId
}) => {
  const [data, setData] = useState<OutsidePressuresData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // No session to analyse is not an error — a coordinator with no assessment yet lands here.
  const [noSession, setNoSession] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchOutsidePressuresData();
  }, [assessmentId]);

  const fetchOutsidePressuresData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      setNoSession(false);
      const sessionId = assessmentId || (await resolveCurrentSessionId());
      if (!sessionId) {
        setNoSession(true);
        return;
      }
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/risk-intelligence/outside-pressures/${sessionId}`, { headers: authHeaders() });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.outsidePressures) {
        setData(result.outsidePressures);
        // Auto-expand first high-risk category
        const categories = Object.entries(result.outsidePressures.categoryScores);
        const highRiskCategory = categories.find(([_, score]) => (score as CategoryScore).score >= 60);
        if (highRiskCategory) {
          setExpandedCategories(new Set([highRiskCategory[0]]));
        }
      } else {
        throw new Error(result.error || 'Failed to load outside pressures data');
      }
    } catch (error) {
      console.error('❌ Error fetching outside pressures data:', error);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  const getCategoryIcon = (categoryName: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      'Regulatory & Compliance': <Shield aria-hidden="true" style={{ width: '20px', height: '20px' }} />,
      'Evolving Threat Landscape': <ShieldAlert aria-hidden="true" style={{ width: '20px', height: '20px' }} />,
      'Geopolitical Risks': <Globe2 style={{ width: '20px', height: '20px' }} />,
      'Supply Chain Vulnerabilities': <Briefcase aria-hidden="true" style={{ width: '20px', height: '20px' }} />,
      'Market & Economic Conditions': <DollarSign aria-hidden="true" style={{ width: '20px', height: '20px' }} />,
      'Public Perception & Reputation': <Users aria-hidden="true" style={{ width: '20px', height: '20px' }} />,
      'Technological Advancements': <Cpu style={{ width: '20px', height: '20px' }} />
    };
    return iconMap[categoryName] || <Info aria-hidden="true" style={{ width: '20px', height: '20px' }} />;
  };

  // Convert a factor impact label to a numeric severity (higher = worse)
  const impactSeverity = (impact: string): number => {
    if (impact === 'Critical' || impact === 'Future Critical') return 3;
    if (impact === 'High') return 2;
    if (impact === 'Medium') return 1;
    return 0;
  };

  // Effective risk level for a category: the higher of its score-based level
  // and the worst factor impact it contains (prevents green categories with Critical factors)
  // A null score means no source measures this category. It gets its own level (-1) and is
  // never raised by its factors' impact labels, which are fixed text, not measurements.
  const getCategoryRiskLevel = (score: number | null, categoryName?: string): number => {
    if (!isMeasured(score)) return NOT_MEASURED_LEVEL;
    let level = score >= 81 ? 3 : score >= 61 ? 2 : score >= 41 ? 1 : 0;
    if (categoryName && data?.categories[categoryName]) {
      const worstImpact = Math.max(
        ...data.categories[categoryName].factors.map(f => impactSeverity(f.impact))
      );
      level = Math.max(level, worstImpact);
    }
    return level;
  };

  const riskLevelFor = (score: number | null, categoryName?: string): number => {
    if (!isMeasured(score)) return NOT_MEASURED_LEVEL;
    return categoryName ? getCategoryRiskLevel(score, categoryName) : (score >= 81 ? 3 : score >= 61 ? 2 : score >= 41 ? 1 : 0);
  };

  const getRiskColor = (score: number | null, categoryName?: string): string => {
    const level = riskLevelFor(score, categoryName);
    if (level === NOT_MEASURED_LEVEL) return 'var(--ra-text-secondary)';
    if (level >= 3) return 'var(--ra-danger)';
    if (level >= 2) return 'var(--ra-warning)';
    return 'var(--ra-success)';
  };

  const getRiskBgColor = (score: number | null, categoryName?: string): string => {
    const level = riskLevelFor(score, categoryName);
    if (level === NOT_MEASURED_LEVEL) return 'rgba(255, 255, 255, 0.04)';
    if (level >= 3) return 'rgba(255, 68, 68, 0.08)';
    if (level >= 2) return 'rgba(255, 165, 0, 0.08)';
    return 'rgba(0, 255, 136, 0.08)';
  };

  const getRiskLabel = (score: number | null, categoryName?: string): string => {
    const level = riskLevelFor(score, categoryName);
    if (level === NOT_MEASURED_LEVEL) return NOT_MEASURED;
    if (level >= 3) return 'Critical';
    if (level >= 2) return 'Elevated';
    return 'Manageable';
  };

  const getRiskEmoji = (score: number | null, categoryName?: string): string => {
    const level = riskLevelFor(score, categoryName);
    if (level === NOT_MEASURED_LEVEL) return '⚪';
    if (level >= 3) return '🔴';
    if (level >= 2) return '🟡';
    return '🟢';
  };

  const getRiskDescription = (score: number | null, categoryName?: string): string => {
    const level = riskLevelFor(score, categoryName);
    if (level === NOT_MEASURED_LEVEL) return 'No data source measures this category yet';
    if (level >= 3) return 'Immediate attention required';
    if (level >= 2) return 'Requires monitoring and planning';
    return 'Within acceptable risk tolerance';
  };

  const bandLabelFor = (score: number | null): string => (isMeasured(score) ? getRiskBandLabel(score) : NOT_MEASURED);

  if (loading) {
    return (
      <div style={{ padding: '32px', backgroundColor: 'var(--ra-deep-blue)', minHeight: '100vh' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            backgroundColor: 'var(--ra-glass-bg)', 
            borderRadius: '8px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
            padding: '48px', 
            textAlign: 'center' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
              <RefreshCw aria-hidden="true" style={{ width: '32px', height: '32px', color: 'var(--ra-cyber-aqua)', animation: 'spin 1s linear infinite' }} />
              <span style={{ marginLeft: '16px', fontSize: '20px', fontWeight: '600', color: 'var(--ra-text-primary)' }}>
                Loading outside pressures analysis...
              </span>
            </div>
            <p style={{ color: 'var(--ra-text-secondary)', margin: 0 }}>Analyzing external risk factors</p>
          </div>
        </div>
      </div>
    );
  }

  if (noSession) {
    return (
      <div style={{ padding: '32px', backgroundColor: 'var(--ra-deep-blue)', minHeight: '100vh' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            backgroundColor: 'var(--ra-glass-bg)',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: '48px',
            textAlign: 'center'
          }}>
            <Info aria-hidden="true" style={{ width: '48px', height: '48px', color: 'var(--ra-cyber-aqua)', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--ra-text-primary)', marginBottom: '8px' }}>
              No assessment selected
            </h2>
            <p style={{ color: 'var(--ra-text-secondary)', margin: 0 }}>
              Outside pressures are analysed for an assessment. Open one from your assessment dashboard to see its analysis.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: '32px', backgroundColor: 'var(--ra-deep-blue)', minHeight: '100vh' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            backgroundColor: 'var(--ra-glass-bg)', 
            borderRadius: '8px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
            padding: '48px', 
            textAlign: 'center' 
          }}>
            <XCircle aria-hidden="true" style={{ width: '48px', height: '48px', color: 'var(--ra-danger)', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--ra-text-primary)', marginBottom: '8px' }}>
              Analysis Error
            </h2>
            <p style={{ color: 'var(--ra-text-secondary)', marginBottom: '24px' }}>
              {error || 'Failed to load outside pressures data'}
            </p>
            <button 
              onClick={fetchOutsidePressuresData}
              style={{
                padding: '8px 16px',
                background: 'var(--ra-gradient-cta)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Retry Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }

  const filteredCategories = selectedCategory === 'All' 
    ? data.categories 
    : { [selectedCategory]: data.categories[selectedCategory] };

  return (
    <div style={{ padding: '32px', backgroundColor: 'var(--ra-deep-blue)', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Executive Summary Banner */}
        <div style={{
          marginBottom: '32px',
          padding: '24px',
          background: 'var(--ra-glass-bg)',
          border: '1px solid var(--ra-glass-border)',
          borderRadius: '12px',
          color: 'var(--ra-text-primary)',
          backdropFilter: 'blur(12px)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                Outside Pressures Analysis - Executive Risk Summary <HelpTooltip contentKey="defense.outsidePressures" />
              </h1>
              <p style={{ color: 'var(--ra-text-muted)', fontSize: '14px', margin: 0 }}>
                External factors impacting cybersecurity risk profile
              </p>
              <div style={{ marginTop: '8px' }}>
                <span style={{
                  backgroundColor: 'rgba(0, 217, 255, 0.1)',
                  border: '1px solid rgba(0, 217, 255, 0.2)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: 'var(--ra-cyber-aqua)',
                }}>
                  Based on Assessment Data • {data.totalFactors} External Factors Analyzed
                </span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '48px', fontWeight: 'bold', lineHeight: '1' }}>
                {isMeasured(data.overallScore) ? data.overallScore : NOT_MEASURED}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--ra-text-muted)' }}>
                External Risk Score
              </div>
            </div>
          </div>
          
          {/* Metrics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div style={{ 
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--ra-glass-border)',
              borderRadius: '8px', 
              padding: '16px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.totalFactors}</div>
                  <div style={{ fontSize: '12px', color: 'var(--ra-text-muted)' }}>Total Factors</div>
                </div>
                <Globe aria-hidden="true" style={{ width: '32px', height: '32px', color: 'var(--ra-cyber-aqua)' }} />
              </div>
            </div>
            
            <div style={{ 
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--ra-glass-border)',
              borderRadius: '8px', 
              padding: '16px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.criticalFactors}</div>
                  <div style={{ fontSize: '12px', color: 'var(--ra-text-muted)' }}>Critical Factors</div>
                </div>
                <AlertCircle aria-hidden="true" style={{ width: '32px', height: '32px', color: '#f87171' }} />
              </div>
            </div>
            
            <div style={{ 
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--ra-glass-border)',
              borderRadius: '8px', 
              padding: '16px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.emergingThreats}</div>
                  <div style={{ fontSize: '12px', color: 'var(--ra-text-muted)' }}>Emerging Threats</div>
                </div>
                <TrendingUp aria-hidden="true" style={{ width: '32px', height: '32px', color: '#fbbf24' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Strategic Recommendation */}
        {data.executiveSummary && data.executiveSummary.keyInsights.length > 0 && (
          <div style={{
            marginBottom: '24px',
            padding: '20px 24px',
            background: 'rgba(0, 217, 255, 0.06)',
            border: '1px solid rgba(0, 217, 255, 0.15)',
            borderRadius: '12px',
            color: 'var(--ra-text-primary)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <Target aria-hidden="true" style={{ width: '24px', height: '24px', color: 'var(--ra-cyber-aqua)' }} />
              <h3 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>Strategic Recommendation</h3>
            </div>
            <p style={{ color: 'var(--ra-text-secondary)', margin: 0, lineHeight: '1.6' }}>
              {data.executiveSummary.executiveRecommendation}
            </p>
          </div>
        )}

        {/* Category Filter Tabs */}
        <div style={{ 
          backgroundColor: 'var(--ra-glass-bg)', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
          padding: '16px', 
          marginBottom: '24px' 
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <button
              onClick={() => setSelectedCategory('All')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                backgroundColor: selectedCategory === 'All' ? '#3b82f6' : '#f3f4f6',
                color: selectedCategory === 'All' ? 'white' : '#374151',
                transition: 'all 0.2s ease'
              }}
            >
              All Categories
            </button>
            {Object.keys(data.categories).map((categoryName) => {
              const categoryScore = data.categoryScores[categoryName];
              const isSelected = selectedCategory === categoryName;
              return (
                <button
                  key={categoryName}
                  onClick={() => setSelectedCategory(categoryName)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#3b82f6' : '#f3f4f6',
                    color: isSelected ? 'white' : '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {getCategoryIcon(categoryName)}
                  <span>{categoryName}</span>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : getRiskBgColor(categoryScore.score),
                    color: isSelected ? 'white' : getRiskColor(categoryScore.score)
                  }}>
                    {getRiskEmoji(categoryScore.score)} {getRiskLabel(categoryScore.score)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Categories */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {Object.entries(filteredCategories).map(([categoryName, categoryData]) => {
            const categoryScore = data.categoryScores[categoryName];
            const isExpanded = expandedCategories.has(categoryName);
            
            return (
              <div key={categoryName} style={{ 
                backgroundColor: 'var(--ra-glass-bg)', 
                borderRadius: '8px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
                overflow: 'hidden' 
              }}>
                <button
                  onClick={() => toggleCategory(categoryName)}
                  style={{
                    width: '100%',
                    padding: '20px 24px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--ra-deep-blue)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ 
                        padding: '12px', 
                        borderRadius: '8px',
                        backgroundColor: getRiskBgColor(categoryScore.score, categoryName)
                      }}>
                        <div style={{ color: getRiskColor(categoryScore.score, categoryName) }}>
                          {getCategoryIcon(categoryName)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--ra-text-primary)', marginBottom: '4px', margin: 0 }}>
                          {categoryName}
                        </h3>
                        <p style={{ fontSize: '14px', color: 'var(--ra-text-secondary)', margin: 0 }}>
                          {categoryData.description}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: getRiskColor(categoryScore.score, categoryName),
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {getRiskEmoji(categoryScore.score, categoryName)} {getRiskLabel(categoryScore.score, categoryName)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', maxWidth: '200px' }}>
                          {getRiskDescription(categoryScore.score, categoryName)}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--ra-text-muted)', marginTop: '2px' }}>
                          Score: {bandLabelFor(categoryScore.score)}
                        </div>
                      </div>
                      <ChevronRight aria-hidden="true" style={{ 
                        width: '20px', 
                        height: '20px', 
                        color: 'var(--ra-text-muted)',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }} />
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div style={{ 
                    borderTop: '1px solid var(--ra-glass-border)',
                    padding: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {categoryData.factors.map((factor) => (
                        <div key={factor.id} style={{ 
                          backgroundColor: 'var(--ra-glass-bg)', 
                          borderRadius: '8px', 
                          padding: '16px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ flex: 1, paddingRight: '16px' }}>
                              <h4 style={{ fontSize: '16px', fontWeight: '500', color: 'var(--ra-text-primary)', marginBottom: '8px', margin: 0 }}>
                                {factor.name}
                              </h4>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  backgroundColor: factor.impact === 'Critical' || factor.impact === 'Future Critical'
                                    ? 'rgba(220, 38, 38, 0.15)' : factor.impact === 'High'
                                    ? 'rgba(245, 158, 11, 0.15)' : factor.impact === 'Medium'
                                    ? 'rgba(245, 158, 11, 0.10)' : 'rgba(16, 185, 129, 0.15)',
                                  color: factor.impact === 'Critical' || factor.impact === 'Future Critical'
                                    ? '#f87171' : factor.impact === 'High'
                                    ? '#fbbf24' : factor.impact === 'Medium'
                                    ? '#fbbf24' : '#34d399'
                                }}>
                                  {factor.impact}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  {factor.trend.includes('Rapidly') ? (
                                    <ArrowUp aria-hidden="true" style={{ width: '16px', height: '16px', color: 'var(--ra-danger)' }} />
                                  ) : factor.trend.includes('Stable') ? (
                                    <Minus aria-hidden="true" style={{ width: '16px', height: '16px', color: 'var(--ra-cyber-aqua)' }} />
                                  ) : (
                                    <Info aria-hidden="true" style={{ width: '16px', height: '16px', color: 'var(--ra-text-secondary)' }} />
                                  )}
                                  <span style={{ fontSize: '14px', color: 'var(--ra-text-secondary)' }}>{factor.trend}</span>
                                </div>
                              </div>
                            </div>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end'
                            }}>
                              <div style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: getRiskColor(factor.currentScore),
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                {getRiskEmoji(factor.currentScore)}
                                {getRiskLabel(factor.currentScore)}
                              </div>
                              <div style={{
                                fontSize: '11px',
                                color: 'var(--ra-text-muted)'
                              }}>
                                {bandLabelFor(factor.currentScore)}
                              </div>
                            </div>
                          </div>
                          
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between', 
                              fontSize: '12px', 
                              color: 'var(--ra-text-secondary)', 
                              marginBottom: '4px' 
                            }}>
                              <span>Risk Level</span>
                              <span>{isMeasured(factor.currentScore) ? `${factor.currentScore}/${factor.maxScore}` : NOT_MEASURED}</span>
                            </div>
                            <div style={{ 
                              width: '100%', 
                              height: '8px', 
                              backgroundColor: 'rgba(255, 255, 255, 0.08)', 
                              borderRadius: '4px', 
                              overflow: 'hidden' 
                            }}>
                              <div style={{ 
                                height: '100%', 
                                width: `${isMeasured(factor.currentScore) ? (factor.currentScore / factor.maxScore) * 100 : 0}%`,
                                backgroundColor: getRiskColor(factor.currentScore),
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                          </div>
                          
                          <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--ra-text-secondary)' }}>
                            <span style={{ fontWeight: '500' }}>Measurement:</span> {factor.measurement}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OutsidePressuresCenter;